import { projectId } from './supabase/info';
import type { DecisionOutcome, DecisionRecord } from './decisionGrid';

/**
 * Discovery grid cell threads (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4.2):
 * checked marks, follow-ups and replies, plus the consultant digest trigger.
 */

export interface ThreadMessage {
  by: string;
  byName?: string;
  role: 'consultant' | 'client';
  text: string;
  at: string;
}

export interface CellThread {
  cellId: string;
  followUp: { open: boolean; openedAt?: string; closedAt?: string; messages: ThreadMessage[] } | null;
  checked: { by: string; byName?: string; at: string } | null;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

async function call(path: string, accessToken: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

const cellPath = (companyId: string, cellId: string) => `/discovery-grid/${companyId}/cells/${encodeURIComponent(cellId)}`;

export interface GuidedQuestionDef {
  id: string;
  questionText: string;
  questionType: 'text' | 'number' | 'textarea' | 'boolean' | 'multiple_choice' | 'checkbox' | 'select' | 'list_builder' | string;
  options?: string[];
  placeholder?: string;
  helpText?: string;
}

/** The category's guided questions (same assembly the grid uses), for editing an answer in place. */
export function getGuidedQuestions(companyId: string, category: string, accessToken: string): Promise<GuidedQuestionDef[]> {
  return call(`/company-data/${companyId}/guided-questions/${encodeURIComponent(category)}`, accessToken);
}

/** Save (or change) a guided answer; the server resets the check and feeds the digest like any client answer. */
export function saveGuidedAnswer(
  companyId: string,
  category: string,
  input: { questionId: string; question: string; answer: unknown; answeredBy?: string; answeredByName?: string },
  accessToken: string,
): Promise<void> {
  const a = input.answer;
  const confidence = a === null || a === undefined || a === '' || (Array.isArray(a) && a.length === 0) ? 'low' : typeof a === 'string' && a.trim().length < 10 ? 'medium' : 'high';
  return call(`/company-data/${companyId}/ai-context/category/${encodeURIComponent(category)}/answer`, accessToken, {
    method: 'POST',
    body: JSON.stringify({ ...input, confidence }),
  });
}

export function getCellThread(companyId: string, cellId: string, accessToken: string): Promise<CellThread> {
  return call(`${cellPath(companyId, cellId)}/thread`, accessToken);
}

/** Consultant looked at the answer (level 3 → 4). */
export function checkCell(companyId: string, cellId: string, accessToken: string): Promise<CellThread> {
  return call(`${cellPath(companyId, cellId)}/check`, accessToken, { method: 'POST', body: '{}' });
}

/** Consultant asks for more (→ level 2, ball to the client). */
export function askFollowUp(companyId: string, cellId: string, text: string, accessToken: string): Promise<CellThread> {
  return call(`${cellPath(companyId, cellId)}/follow-up`, accessToken, { method: 'POST', body: JSON.stringify({ text }) });
}

/** Client replies (→ level 3, unchecked). */
export function replyFollowUp(companyId: string, cellId: string, text: string, accessToken: string, engagementId?: string | null): Promise<CellThread> {
  return call(`${cellPath(companyId, cellId)}/reply`, accessToken, { method: 'POST', body: JSON.stringify({ text, engagementId: engagementId ?? null }) });
}

/** Send the signed-in consultant their digest now, ignoring the hourly/daily throttles. */
export function runMyDigest(accessToken: string): Promise<{ inApp: number; emails: number }> {
  return call('/discovery-digest/run-mine', accessToken, { method: 'POST', body: '{}' });
}

export interface AnswerToCheck {
  companyId: string;
  companyName: string;
  engagementId: string | null;
  cellId: string;
  text: string;
  blockName: string;
  category: string;
  answeredAt?: string;
  answeredBy?: string;
  preview?: string;
}

export function listAnswersToCheck(accessToken: string, limit = 25): Promise<{ total: number; items: AnswerToCheck[] }> {
  return call(`/discovery-grid/answers-to-check?limit=${limit}`, accessToken);
}

// ---------- Scope (spec §4.10) ----------

export type DescopeDestination = 'change_order' | 'engagement' | 'client' | 'dropped';

export interface DescopeInput {
  to: DescopeDestination;
  ref?: string;
  label?: string;
  reason?: string;
  engagementId?: string | null;
}

export function descopeCell(companyId: string, cellId: string, input: DescopeInput, accessToken: string): Promise<any> {
  return call(`${cellPath(companyId, cellId)}/descope`, accessToken, { method: 'POST', body: JSON.stringify(input) });
}

/** One config item as stored (lock fields, status and unlock history included), for the drawer. */
export function getConfigItem(companyId: string, itemId: string, accessToken: string): Promise<any> {
  return call(`/config-items/${companyId}/${encodeURIComponent(itemId)}`, accessToken).then((b) => b.item);
}

/** Unlock a locked config item (consultants): discovery reopens for it; the reason is kept in its unlock history. */
export function unlockConfigItem(companyId: string, itemId: string, input: { reason: string; createChangeOrder: boolean; engagementId?: string | null }, accessToken: string): Promise<{ item: any; changeOrder: UnlockChangeOrder | null }> {
  return call(`/config-items/${companyId}/${encodeURIComponent(itemId)}/unlock`, accessToken, { method: 'POST', body: JSON.stringify(input) }).then((b) => ({ item: b.item, changeOrder: b.changeOrder ?? null }));
}

/** What an unlock did on the engagement's change orders. */
export interface UnlockChangeOrder { action: 'created' | 'added'; id: string; changeOrderId: string }

/** The engagement's open draft change order, if there is one: the unlock modal offers to add to it. */
export function getOpenDraftChangeOrder(engagementId: string, accessToken: string): Promise<{ id: string; changeOrderId: string } | null> {
  return call(`/engagements/${encodeURIComponent(engagementId)}/change-orders`, accessToken).then((b) => {
    const draft = (b.changeOrders ?? []).find((co: any) => co.status === 'draft');
    return draft ? { id: draft.id, changeOrderId: draft.changeOrderId } : null;
  });
}

/** Move a config item through the build backlog (clients configure and approve, the firm builds). */
export function setImplementationStatus(companyId: string, itemId: string, input: { status: string; comment?: string }, accessToken: string): Promise<any> {
  return call(`/config-items/${companyId}/${encodeURIComponent(itemId)}/implementation-status`, accessToken, { method: 'PATCH', body: JSON.stringify(input) }).then((b) => b.item);
}

/** The company's config items as stored (the list route). */
export function listConfigItems(companyId: string, accessToken: string): Promise<any[]> {
  return call(`/config-items/${companyId}`, accessToken).then((b) => (Array.isArray(b.items) ? b.items : []));
}

export function restoreCell(companyId: string, cellId: string, accessToken: string): Promise<any> {
  return call(`${cellPath(companyId, cellId)}/restore`, accessToken, { method: 'POST', body: '{}' });
}

export function descopeBlock(companyId: string, blockId: string, input: DescopeInput, accessToken: string): Promise<any> {
  return call(`/discovery-grid/${companyId}/blocks/${encodeURIComponent(blockId)}/descope`, accessToken, { method: 'POST', body: JSON.stringify(input) });
}

export function restoreBlock(companyId: string, blockId: string, accessToken: string): Promise<any> {
  return call(`/discovery-grid/${companyId}/blocks/${encodeURIComponent(blockId)}/restore`, accessToken, { method: 'POST', body: '{}' });
}

export interface LeftoverCell {
  cellId: string;
  text: string;
  kind: 'question' | 'artifact';
  level: number;
  levelLabel: string;
  answerPreview?: string;
  descoped: { to: DescopeDestination; ref?: string; label?: string; reason?: string; at: string } | null;
}

/** Server-side proposal (spec §4.10 step 1): the target document's scope item this block most plausibly satisfies. */
export interface ProposedMatch {
  blockId: string;
  scopeItemId: string;
  scopeItemLabel: string;
  scopeItemSource: 'hlo' | 'in_scope' | 'suggested_config_item' | 'scope_changes' | 'scope_description';
  score: number;
  matchedCellIds: string[];
  outcome: 'carry';
}

export interface LeftoverBlock {
  blockId: string;
  name: string;
  category: string;
  guided: boolean;
  total: number;
  cells: LeftoverCell[];
  proposed?: ProposedMatch;
}

export interface LeftoversResult {
  engagementId: string | null;
  blocks: LeftoverBlock[];
  /** How many scope items the target document contributed to matching (0 without a target) */
  scopeItems?: number;
}

/** With a `target`, the server matches each block against that document's scope items and fills `proposed`. */
export function listLeftovers(companyId: string, fromEngagementId: string | null, accessToken: string, target?: ReconcileTarget | null): Promise<LeftoversResult> {
  const params = new URLSearchParams();
  if (fromEngagementId) params.set('fromEngagementId', fromEngagementId);
  if (target) {
    params.set('targetType', target.type);
    if (target.engagementId) params.set('targetEngagementId', target.engagementId);
    if (target.ref) params.set('targetRef', target.ref);
  }
  const q = params.toString();
  return call(`/discovery-grid/${companyId}/leftovers${q ? `?${q}` : ''}`, accessToken);
}

export type ReconcileOutcome = 'carry' | 'leave' | 'client' | 'drop';

export interface ReconcileDecision {
  cellId?: string;
  blockId?: string;
  outcome: ReconcileOutcome;
  reason?: string;
  /** Display text of the cell or block, kept on the scope map */
  text?: string;
  /** The scope item this decision satisfies, when a proposal was accepted */
  scopeItemId?: string;
  scopeItemLabel?: string;
}

/** One recorded decision on a document's `scopeMap` (engagement or change order). */
export interface ScopeMapEntry extends ReconcileDecision {
  at: string;
  by: string;
  byName?: string;
  fromEngagementId?: string | null;
}

export interface ReconcileTarget {
  type: 'engagement' | 'change_order';
  engagementId: string | null;
  ref?: string;
  label?: string;
}

export function reconcileScope(companyId: string, target: ReconcileTarget, fromEngagementId: string | null, decisions: ReconcileDecision[], accessToken: string): Promise<{ carried: number; toClient: number; dropped: number; left: number }> {
  return call(`/discovery-grid/${companyId}/reconcile`, accessToken, { method: 'POST', body: JSON.stringify({ target, fromEngagementId, decisions }) });
}

/** Deep link to one cell on a company's discovery grid (consultant client-detail route). */
export function cellDeepLink(companyId: string, cellId: string): string {
  return `/clients/${companyId}?tab=config-items&cell=${encodeURIComponent(cellId)}`;
}

/** The decision recorded on a cell's architecture choice (RESTORE_PLAN R2.7), or null. */
export async function getCellDecision(companyId: string, cellId: string, accessToken: string): Promise<DecisionRecord | null> {
  const body = await call(`/decision-history/${encodeURIComponent(companyId)}?cellId=${encodeURIComponent(cellId)}`, accessToken);
  return (body.decisions ?? [])[0] ?? null;
}

/** Record the client's decision: follow the recommendation, proceed anyway, or ask for review. */
export async function saveCellDecision(
  companyId: string,
  input: { cellId: string; decisionNodeId: string; selectedOption: string; outcome: DecisionOutcome; reason?: string },
  accessToken: string,
): Promise<DecisionRecord> {
  const body = await call(`/decision-history/${encodeURIComponent(companyId)}/save`, accessToken, { method: 'POST', body: JSON.stringify(input) });
  return body.decision;
}
